import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { useAddContractSearchParams } from '@/hooks/use-add-contract-search-params';

type AddContractContextValue = {
  isSheetOpen: boolean;
  channelId: number | undefined;
  channelName: string | undefined;
  setSheetOpen: (open: boolean) => void;
  setSelectedChannel: (id: number | undefined, name?: string) => void;
};

const AddContractContext = createContext<AddContractContextValue | undefined>(
  undefined,
);

export function AddContractProvider({ children }: PropsWithChildren) {
  const {
    isSheetOpen,
    channelId,
    channelName,
    setSheetOpen,
    setSelectedChannel,
  } = useAddContractSearchParams();

  const value = useMemo(
    () => ({
      isSheetOpen,
      channelId,
      channelName,
      setSheetOpen,
      setSelectedChannel,
    }),
    [isSheetOpen, channelId, channelName, setSheetOpen, setSelectedChannel],
  );

  return (
    <AddContractContext.Provider value={value}>
      {children}
    </AddContractContext.Provider>
  );
}

export function useAddContractContext(): AddContractContextValue {
  const context = useContext(AddContractContext);
  if (context == null) {
    throw new Error(
      'useAddContractContext must be used within an AddContractProvider',
    );
  }
  return context;
}
